<template>
  <div class="pm-page">
    <!-- Hero: filter, quick actions and how much of the map is complete -->
    <section class="home-hero pm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('processing_map.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title">{{ $t('processes.title') }}</h1>
        <p class="home-hero__summary">{{ $t('processing_map.intro') }}</p>

        <div class="home-search pm-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input
              ref="search"
              v-model="query"
              class="home-search__input"
              :placeholder="$t('processing_map.search_placeholder')"
              :aria-label="$t('processing_map.search_placeholder')"
              autocomplete="off"
              qid="processing-map-search"
          />
          <span class="home-kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>
        </div>

        <div class="home-hero__chips pm-hero__chips">
          <button v-if="isGranted(permissions.PROCESS_CREATE)"
              type="button"
              class="home-chip home-chip--light"
              qid="processing-map-add-process"
              @click="addProcess">
            <font-awesome-icon icon="plus"/>{{ $t('processing_map.actions.process') }}
          </button>
          <button v-if="isGranted(permissions.ACTIVITY_CREATE)"
              type="button"
              class="home-chip"
              qid="processing-map-add-activity"
              @click="addActivity(null)">
            <font-awesome-icon icon="plus"/>{{ $t('processing_map.actions.activity') }}
          </button>
          <router-link v-if="isGranted(permissions.PROCESS_GROUP_CREATE)"
              :to="{ name: 'maintenance.process_groups.create' }"
              class="home-chip"
              qid="processing-map-add-group">
            <font-awesome-icon icon="plus"/>{{ $t('processing_map.actions.process_group') }}
          </router-link>
          <button v-if="getClient.projectModule"
              type="button"
              class="home-chip"
              qid="processing-map-start-project"
              @click="startProject">
            <font-awesome-icon icon="calendar-alt"/>{{ $t('processing_map.actions.start_project') }}
          </button>
          <b-dropdown v-if="getClient.projectModule || isGranted(permissions.MAINTENANCE_VIEW)"
              variant="link"
              toggle-class="home-chip pm-chip-toggle"
              no-caret
              qid="processing-map-more">
            <template #button-content>
              <font-awesome-icon icon="file-download"/>{{ $t('processing_map.actions.reports_and_lists') }}
              <font-awesome-icon icon="angle-down"/>
            </template>
            <template v-if="isGranted(permissions.MAINTENANCE_VIEW)">
              <b-dropdown-header>{{ $t('processes.processing_activities.reports_button') }}</b-dropdown-header>
              <b-dropdown-item @click="$refs.actions.openProcessModal()" qid="download_process_overview_report">{{ $t('processes.processing_activities.reports_button_process_overview') }}</b-dropdown-item>
              <b-dropdown-item @click="$refs.actions.openArticleModal()" qid="download_article_report">{{ $t('processes.processing_activities.reports_button_article_30') }}</b-dropdown-item>
              <b-dropdown-header>{{ $t('processes.processing_activities.lists_button') }}</b-dropdown-header>
              <b-dropdown-item @click="$refs.actions.openDownloadProcesses()">{{ $t('processes.processing_activities.lists_button_processes') }}</b-dropdown-item>
              <b-dropdown-item @click="$refs.actions.openDownloadActivities()">{{ $t('processes.processing_activities.lists_button_activities') }}</b-dropdown-item>
            </template>
            <template v-if="getClient.projectModule">
              <b-dropdown-divider v-if="isGranted(permissions.MAINTENANCE_VIEW)"></b-dropdown-divider>
              <b-dropdown-item @click="$refs.actions.openRunningProjects()">{{ $t('processes.processing_activities.view_running_projects') }}</b-dropdown-item>
            </template>
          </b-dropdown>
        </div>
      </div>

      <div v-if="map.gdpr && completeness.total" class="home-week pm-progress">
        <div class="home-week__top">
          <svg class="home-ring"
              width="96"
              height="96"
              viewBox="0 0 96 96"
              aria-hidden="true">
            <circle class="home-ring__track"
                cx="48"
                cy="48"
                r="42"/>
            <circle v-if="completeness.done"
                class="home-ring__value"
                cx="48"
                cy="48"
                r="42"
                :stroke-dasharray="ringDash(completeness.done, completeness.total, 42)"/>
          </svg>
          <b class="home-week__count pm-progress__count">{{ completeness.done }}/{{ completeness.total }}</b>
          <div>
            <div class="home-week__title">{{ $t('processing_map.progress.title') }}</div>
            <div class="home-week__text">{{ map.system_module ? $t('processing_map.progress.text') : $t('processing_map.progress.text_no_systems') }}</div>
          </div>
        </div>
        <div class="home-week__stats">
          <div><b>{{ map.groups.length }}</b>{{ $tc('processing_map.progress.groups', map.groups.length) }}</div>
          <div><b>{{ map.processes.length }}</b>{{ $tc('processing_map.progress.processes', map.processes.length) }}</div>
          <div><b>{{ map.activities.length }}</b>{{ $tc('processing_map.progress.activities', map.activities.length) }}</div>
        </div>
      </div>
    </section>

    <!-- Needs attention -->
    <div v-if="attention.length" class="pm-attention">
      <button
          v-for="item in attention"
          :key="item.key"
          type="button"
          class="pm-att"
          :qid="'processing-map-attention-' + item.key"
          @click="openAttention(item)"
      >
        <span class="pm-att__dot" :class="'is-' + item.color"></span>
        <span class="pm-att__text">
          <b>{{ item.title }}</b>
          <small>{{ item.text }}</small>
        </span>
        <span class="v2-link">{{ item.action }}</span>
      </button>
    </div>

    <!-- The map -->
    <section class="v2-card pm-map">
      <div class="pm-map__head">
        <div class="pm-map__titles">
          <h3>{{ $t('processing_map.map.title') }}</h3>
          <span class="v2-card__sub">{{ $t('processing_map.map.subtitle') }}</span>
        </div>
        <div class="v2-seg pm-tabs" role="tablist">
          <span v-for="tabItem in tabs"
              :key="tabItem.key"
              role="tab"
              :class="{ on: tab === tabItem.key }"
              :qid="'processing-map-tab-' + tabItem.key"
              @click="tab = tabItem.key">
            {{ tabItem.label }} <i class="pm-tabs__count" :class="{ 'is-warn': tabItem.warn && tabItem.count }">{{ tabItem.count }}</i>
          </span>
        </div>
        <button type="button"
            v-if="!loading && rows.length"
            class="btn btn-secondary btn-sm pm-expand"
            qid="processing-map-expand"
            @click="toggleAll">
          <font-awesome-icon :icon="allOpen ? 'compress' : 'expand'"/>
          {{ allOpen ? $t('processing_map.map.collapse_all') : $t('processing_map.map.expand_all') }}
        </button>
      </div>

      <div v-if="map.gdpr" class="pm-legend">
        <span class="pm-chip pm-chip--roll">{{ $t('processing_map.map.legend_chip') }}</span> {{ $t('processing_map.map.legend_roll_up') }}
      </div>

      <div class="pm-table" role="table">
        <div class="pm-row pm-row--head" :class="gridClass" role="row">
          <div>{{ $t('processing_map.columns.name') }}</div>
          <template v-if="map.gdpr">
            <div>{{ $t('processing_map.columns.legal_basis') }}</div>
            <div>{{ $t('processing_map.columns.data_subjects') }}</div>
          </template>
          <div v-if="map.system_module">{{ $t('processing_map.columns.systems') }}</div>
          <div v-if="map.gdpr">{{ $t('processing_map.columns.retention') }}</div>
          <div v-if="map.gdpr">{{ $t('processing_map.columns.status') }}</div>
          <div v-else>{{ $t('processing_map.columns.owner') }}</div>
        </div>

        <div v-if="loading" class="pm-loading">
          <span v-for="n in 5" :key="n" class="mt-skeleton"></span>
        </div>

        <template v-else>
          <template v-for="row in rows">
            <!-- Process group, or one of the two sections for elements outside the structure -->
            <div v-if="row.type === 'group' || row.type === 'section'"
                 :key="row.key"
                 class="pm-row pm-row--group"
                 :class="[gridClass, row.type === 'section' ? 'pm-row--' + row.section : '']"
                 role="row"
            >
              <div class="pm-name">
                <button type="button"
                    class="pm-twist"
                    :aria-expanded="isOpen(row.key) ? 'true' : 'false'"
                    @click="toggle(row.key)">
                  <font-awesome-icon :icon="isOpen(row.key) ? 'angle-down' : 'angle-right'"/>
                </button>
                <span class="pm-icon" :class="'is-' + row.accent"><font-awesome-icon :icon="row.icon"/></span>
                <span class="pm-name__text">
                  <b>{{ row.name }}</b>
                  <small>{{ row.meta }}</small>
                </span>
              </div>
              <template v-if="row.type === 'group'">
                <template v-if="map.gdpr">
                  <div class="pm-cell"><pm-chips v-if="!isOpen(row.key)"
                      :items="row.legalBasis"
                      roll
                      :max="1"/></div>
                  <div class="pm-cell"><pm-chips v-if="!isOpen(row.key)"
                      :items="row.dataSubjects"
                      roll
                      :max="2"/></div>
                </template>
                <div v-if="map.system_module" class="pm-cell"><pm-chips v-if="!isOpen(row.key)"
                    :items="row.systems"
                    roll
                    :max="2"/></div>
                <div v-if="map.gdpr" class="pm-cell"></div>
                <div v-if="map.gdpr" class="pm-cell">
                  <span v-if="row.total" class="pm-bar"><span class="pm-bar__track"><span class="pm-bar__fill" :style="{ width: row.pct + '%' }"></span></span>{{ row.pct }}%</span>
                </div>
                <div v-else class="pm-cell"></div>
              </template>
              <div v-else class="pm-section-text">
                <span>{{ row.text }}</span>
              </div>
            </div>

            <!-- Process -->
            <div v-else-if="row.type === 'process'"
                :key="row.key"
                class="pm-row pm-row--process"
                :class="gridClass"
                role="row">
              <div class="pm-name" :style="{ paddingLeft: row.indent + 'px' }">
                <button type="button"
                    class="pm-twist"
                    :class="{ 'is-empty': !row.activityCount }"
                    :aria-expanded="isOpen(row.key) ? 'true' : 'false'"
                    @click="toggle(row.key)">
                  <font-awesome-icon v-if="row.activityCount" :icon="isOpen(row.key) ? 'angle-down' : 'angle-right'"/>
                </button>
                <span class="pm-icon pm-icon--small"><font-awesome-icon icon="cube"/></span>
                <router-link :to="{ name: 'processes.view', params: { slug: row.process.slug }}" class="pm-name__link">{{ row.process.name }}</router-link>
                <span v-if="!isOpen(row.key) || !row.activityCount" class="v2-tag v2-tag--grey">{{ $tc('processing_map.map.activity_count', row.activityCount, { count: row.activityCount }) }}</span>
              </div>
              <template v-if="map.gdpr">
                <div class="pm-cell">
                  <pm-chips v-if="row.process.privacy"
                      :items="row.legalBasis"
                      kind="lb"
                      :max="1"/>
                  <span v-if="row.process.privacy && !row.legalBasis.length" class="pm-chip pm-chip--miss">{{ $t('processing_map.map.missing') }}</span>
                </div>
                <div class="pm-cell"><pm-chips :items="row.dataSubjects" roll :max="2"/></div>
              </template>
              <div v-if="map.system_module" class="pm-cell"><pm-chips :items="row.systems" roll :max="2"/></div>
              <div v-if="map.gdpr" class="pm-cell"><pm-chips :items="row.retention" roll :max="1"/></div>
              <div v-if="map.gdpr" class="pm-cell"><pm-status :status="row.status"/></div>
              <div v-else class="pm-cell pm-owner">{{ row.process.owner ? row.process.owner.name : '' }}</div>
            </div>

            <!-- Activity -->
            <div v-else-if="row.type === 'activity'"
                :key="row.key"
                class="pm-row pm-row--activity"
                :class="[gridClass, row.orphan ? 'pm-row--orphan' : '']"
                role="row">
              <div class="pm-name" :style="{ paddingLeft: row.indent + 'px' }">
                <span class="pm-dot" :class="{ 'is-amber': row.orphan }"></span>
                <router-link :to="{ name: 'activities.view', params: { slug: row.activity.slug }}" class="pm-name__link pm-name__link--activity">{{ row.activity.name }}</router-link>
                <span v-if="row.alsoIn" class="pm-shared" :title="row.alsoIn"><font-awesome-icon icon="link"/>{{ $t('processing_map.map.also_in', { names: row.alsoIn }) }}</span>
              </div>
              <template v-if="map.gdpr">
                <div class="pm-cell">
                  <button v-if="row.orphan && canLink"
                      type="button"
                      class="btn btn-sm btn-success pm-link-btn"
                      :qid="'processing-map-link-' + row.activity.slug"
                      @click="openLink(row.activity)">
                    <font-awesome-icon icon="link"/> {{ $t('processing_map.unlinked.link') }}
                  </button>
                  <span v-else-if="row.orphan" class="pm-inherit">{{ $t('processing_map.unlinked.no_legal_basis') }}</span>
                  <span v-else-if="row.activity.privacy" class="pm-inherit"><font-awesome-icon icon="angle-double-right"/>{{ $t('processing_map.map.from_process') }}</span>
                </div>
                <div class="pm-cell"><pm-chips :items="row.activity.data_subjects" :max="2"/><span v-if="row.activity.privacy && !row.activity.data_subjects.length" class="pm-chip pm-chip--miss">{{ $t('processing_map.map.missing') }}</span></div>
              </template>
              <div v-if="map.system_module" class="pm-cell"><pm-chips :items="row.activity.systems" :max="2"/></div>
              <div v-if="map.gdpr" class="pm-cell">
                <pm-chips :items="row.activity.retention_rules" :max="1"/>
                <span v-if="row.activity.privacy && !row.activity.retention_rules.length" class="pm-chip pm-chip--miss">{{ $t('processing_map.map.missing') }}</span>
              </div>
              <div v-if="map.gdpr" class="pm-cell"><pm-status :status="row.status"/></div>
              <div v-else class="pm-cell pm-owner">
                <button v-if="row.orphan && canLink"
                    type="button"
                    class="btn btn-sm btn-success pm-link-btn"
                    :qid="'processing-map-link-' + row.activity.slug"
                    @click="openLink(row.activity)">
                  <font-awesome-icon icon="link"/> {{ $t('processing_map.unlinked.link') }}
                </button>
                <template v-else>{{ row.activity.owner ? row.activity.owner.name : '' }}</template>
              </div>
            </div>

            <!-- Add an activity to a process -->
            <div v-else-if="row.type === 'add'"
                :key="row.key"
                class="pm-add"
                :style="{ paddingLeft: row.indent + 'px' }">
              <button type="button"
                  class="pm-add__btn"
                  :qid="'processing-map-add-to-' + row.process.slug"
                  @click="addActivity(row.process)">
                <font-awesome-icon icon="plus"/>{{ $t('processing_map.map.add_activity_to', { name: row.process.name }) }}
              </button>
            </div>
          </template>

          <div v-if="!rows.length" class="home-empty pm-empty">{{ emptyText }}</div>
        </template>
      </div>

      <div v-if="!loading" class="pm-foot">
        <span><span class="pm-icon pm-icon--small"><font-awesome-icon icon="cube"/></span>{{ $t('processing_map.legend.process') }}</span>
        <span><span class="pm-dot"></span>{{ $t('processing_map.legend.activity') }}</span>
        <span><span class="pm-shared"><font-awesome-icon icon="link"/></span>{{ $t('processing_map.legend.shared') }}</span>
      </div>
    </section>

    <!-- Link an activity without a process -->
    <b-modal ref="link-modal" hide-footer @hidden="linkActivity = null">
      <div class="d-block text-center">
        <h4>{{ $t('processing_map.unlinked.modal_title') }}</h4>
      </div>
      <p v-if="linkActivity" class="text-normal mt-2">{{ $t('processing_map.unlinked.modal_text', { name: linkActivity.name }) }}</p>
      <b-alert v-if="linkError" show variant="danger">{{ linkError }}</b-alert>
      <b-form-group>
        <label>{{ $t('processing_map.create_activity.process_label') }}<span class="mandatory"> *</span></label>
        <multiselect
            v-model="linkProcess"
            :allow-empty="false"
            :options="map.processes"
            track-by="slug"
            label="name"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('processing_map.create_activity.process_placeholder')"
            qid="processing-map-link-process"
        ></multiselect>
      </b-form-group>
      <div class="text-right">
        <b-button class="mt-3" variant="secondary" @click="$refs['link-modal'].hide()">{{ $t('processes.delete.cancel') }}</b-button>
        <b-button class="mt-3 ml-3"
            variant="success"
            :disabled="!linkProcess || linking"
            qid="processing-map-link-submit"
            @click="submitLink">
          {{ $t('processing_map.unlinked.link') }}
        </b-button>
      </div>
    </b-modal>

    <ActivityCreateForm ref="createActivity" hideButton @reloadContent="loadMap"></ActivityCreateForm>
    <span class="pm-hidden-trigger">
      <ProcessCreateModal v-if="isGranted(permissions.PROCESS_CREATE)" ref="createProcess" @reloadContent="loadMap"></ProcessCreateModal>
      <CreateProjectModal v-if="getClient.projectModule"
          ref="createProject"
          type="update"
          :types="['update', 'validation']"
          scope="processingActivities"></CreateProjectModal>
    </span>
    <ProcessListActions ref="actions"></ProcessListActions>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import ActivityCreateForm from '@/views/Activities/ActivityCreateForm.vue';
  import ProcessCreateModal from '@/views/Processes/ProcessCreateModal.vue';
  import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
  import ProcessListActions from '@/components/processes/ProcessListActions.vue';

  const INDENT_PROCESS = 28;
  const INDENT_ACTIVITY = 62;

  // Chips for names; "roll" marks values that add up from the activities below
  const PmChips = {
    name: 'PmChips',
    props: {
      items: { type: Array, default: () => [] },
      max: { type: Number, default: 2 },
      roll: { type: Boolean, default: false },
      kind: { type: String, default: '' }
    },
    render(h) {
      const items = this.items || [];
      const shown = items.slice(0, this.max).map(item => h('span', {
        class: ['pm-chip', this.roll ? 'pm-chip--roll' : '', this.kind ? 'pm-chip--' + this.kind : ''],
        attrs: { title: item }
      }, item));
      if (items.length > this.max) {
        shown.push(h('span', { class: 'pm-more', attrs: { title: items.slice(this.max).join(', ') } }, '+' + (items.length - this.max)));
      }
      return h('span', { class: 'pm-chips' }, shown);
    }
  };

  // Complete, or how many things are missing
  const PmStatus = {
    name: 'PmStatus',
    props: { status: { type: Object, default: null } },
    render(h) {
      if (!this.status) {
        return h('span');
      }
      const icon = h('font-awesome-icon', { props: { icon: this.status.ok ? 'check' : 'exclamation-triangle' } });
      return h('span', { class: ['pm-status', this.status.ok ? 'is-ok' : 'is-warn'] }, [icon, this.status.label]);
    }
  };

  export default {
    name: 'ProcessingMap',
    components: {
      ActivityCreateForm,
      ProcessCreateModal,
      CreateProjectModal,
      ProcessListActions,
      PmChips,
      PmStatus
    },
    data: function() {
      return {
        map: { gdpr: false, system_module: false, groups: [], processes: [], activities: [], attention: {} },
        loading: true,
        query: '',
        tab: 'all',
        open: {},
        linkActivity: null,
        linkProcess: null,
        linkError: '',
        linking: false,
        isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canLink() {
        return this.isGranted(this.permissions.ACTIVITY_PROCESS_CREATE) || this.isGranted(this.permissions.PROCESS_ACTIVITY_CREATE);
      },
      // Columns depend on the modules: GDPR (legal basis, data subjects, retention, status) and Systems
      gridClass() {
        return ['pm-grid', this.map.gdpr ? '' : 'pm-grid--basic', this.map.system_module ? '' : 'pm-grid--no-systems'].join(' ').trim();
      },
      processById() {
        return _.keyBy(this.map.processes, 'id');
      },
      activityById() {
        return _.keyBy(this.map.activities, 'id');
      },
      // Missing parts per activity in privacy scope: legal basis (through a process), data subjects, systems (with the Systems module) and retention
      missingByActivity() {
        const missing = {};
        this.map.activities.forEach(activity => {
          if (!this.map.gdpr || !activity.privacy) {
            missing[activity.id] = [];
            return;
          }
          const list = [];
          const hasLegalBasis = activity.process_ids.some(id => this.processById[id] && this.processById[id].legal_basis.length);
          if (!hasLegalBasis) list.push('legal_basis');
          if (!activity.data_subjects.length) list.push('data_subjects');
          // Systems only count when the Systems module is on
          if (this.map.system_module && !activity.systems.length) list.push('systems');
          if (!activity.retention_rules.length) list.push('retention');
          missing[activity.id] = list;
        });
        return missing;
      },
      privacyActivities() {
        return this.map.activities.filter(activity => this.map.gdpr && activity.privacy);
      },
      completeness() {
        const total = this.privacyActivities.length;
        const done = this.privacyActivities.filter(activity => !this.missingByActivity[activity.id].length).length;
        return { done, total };
      },
      orphans() {
        return this.map.activities.filter(activity => !activity.process_ids.length);
      },
      ungrouped() {
        return this.map.processes.filter(process => !process.process_group_id);
      },
      incompleteActivities() {
        return this.map.activities.filter(activity => this.missingByActivity[activity.id].length);
      },
      tabs() {
        const tabs = [{ key: 'all', label: this.$t('processing_map.tabs.all'), count: this.map.activities.length }];
        if (this.map.gdpr) {
          tabs.push({ key: 'missing', label: this.$t('processing_map.tabs.missing'), count: this.incompleteActivities.length, warn: true });
        }
        tabs.push({ key: 'unlinked', label: this.$t('processing_map.tabs.unlinked'), count: this.orphans.length, warn: true });
        return tabs;
      },
      attention() {
        const a = this.map.attention || {};
        const list = [];
        if (a.activities_without_process) {
          list.push({
            key: 'unlinked', color: 'amber', tab: 'unlinked',
            title: this.$tc('processing_map.attention.unlinked', a.activities_without_process, { count: a.activities_without_process }),
            text: this.$t('processing_map.attention.unlinked_text'),
            action: this.$t('processing_map.attention.show')
          });
        }
        if (this.map.gdpr && a.processes_without_legal_basis) {
          list.push({
            key: 'legal_basis', color: 'red', tab: 'missing',
            title: this.$tc('processing_map.attention.no_legal_basis', a.processes_without_legal_basis, { count: a.processes_without_legal_basis }),
            text: this.$t('processing_map.attention.no_legal_basis_text'),
            action: this.$t('processing_map.attention.show')
          });
        }
        if (this.map.gdpr && a.activities_without_retention) {
          list.push({
            key: 'retention', color: 'amber', tab: 'missing',
            title: this.$tc('processing_map.attention.no_retention', a.activities_without_retention, { count: a.activities_without_retention }),
            text: this.$t('processing_map.attention.no_retention_text'),
            action: this.$t('processing_map.attention.show')
          });
        }
        if (a.processes_without_group) {
          list.push({
            key: 'ungrouped', color: 'blue', tab: 'all', open: 'section-ungrouped',
            title: this.$tc('processing_map.attention.ungrouped', a.processes_without_group, { count: a.processes_without_group }),
            text: this.$t('processing_map.attention.ungrouped_text'),
            action: this.$t('processing_map.attention.show')
          });
        }
        return list;
      },
      searching() {
        return this.query.trim().length > 0;
      },
      // Every row key that can be opened, for Expand all
      openableKeys() {
        return this.map.groups.map(group => 'group-' + group.id)
          .concat(this.map.processes.map(process => 'process-' + process.id))
          .concat(['section-ungrouped', 'section-unlinked']);
      },
      allOpen() {
        return this.openableKeys.every(key => this.isOpen(key));
      },
      emptyText() {
        if (this.searching) {
          return this.$t('processing_map.map.no_match', { query: this.query.trim() });
        }
        if (this.tab === 'missing') return this.$t('processing_map.map.nothing_missing');
        if (this.tab === 'unlinked') return this.$t('processing_map.map.nothing_unlinked');
        return this.$t('processing_map.map.empty');
      },
      rows() {
        const query = this.query.trim().toLowerCase();
        const matches = name => !query || (name || '').toLowerCase().indexOf(query) !== -1;
        const rows = [];

        const activityVisible = activity => {
          if (this.tab === 'missing' && !this.missingByActivity[activity.id].length) return false;
          return true;
        };
        const activitiesOf = process => process.activity_ids
          .map(id => this.activityById[id])
          .filter(activity => activity && activityVisible(activity));

        // A process is shown when it, or one of its activities, matches the filter and tab
        const processRows = (process, indent, parentMatches) => {
          const activities = activitiesOf(process);
          const ownMatch = parentMatches || matches(process.name);
          const shownActivities = ownMatch ? activities : activities.filter(activity => matches(activity.name));
          const legalBasisMissing = this.map.gdpr && process.privacy && !process.legal_basis.length;
          if (this.tab === 'missing' && !shownActivities.length && !legalBasisMissing) return [];
          if (!ownMatch && !shownActivities.length) return [];

          const key = 'process-' + process.id;
          const all = activitiesOf(process);
          const out = [{
            type: 'process', key, indent, process,
            activityCount: process.activity_ids.length,
            legalBasis: process.legal_basis.map(this.legalBasisName),
            dataSubjects: _.uniq(_.flatMap(all, 'data_subjects')),
            systems: _.uniq(_.flatMap(all, 'systems')),
            retention: _.uniq(_.flatMap(all, 'retention_rules')),
            status: this.processStatus(process)
          }];
          if (this.isOpen(key) || this.searching) {
            shownActivities.forEach(activity => out.push(this.activityRow(activity, process, indent + INDENT_ACTIVITY - INDENT_PROCESS)));
            if (this.tab === 'all' && !this.searching && this.isGranted(this.permissions.ACTIVITY_CREATE)) {
              out.push({ type: 'add', key: 'add-' + process.id, indent: indent + INDENT_ACTIVITY - INDENT_PROCESS, process });
            }
          }
          return out;
        };

        if (this.tab !== 'unlinked') {
          this.map.groups.forEach(group => {
            const processes = this.map.processes.filter(process => process.process_group_id === group.id);
            const groupMatch = matches(group.name);
            const children = _.flatMap(processes, process => processRows(process, INDENT_PROCESS, groupMatch));
            if (!children.length && !(groupMatch && this.tab === 'all')) return;

            const key = 'group-' + group.id;
            const activities = _.uniqBy(_.flatMap(processes, process => activitiesOf(process)), 'id');
            const privacy = activities.filter(activity => this.map.gdpr && activity.privacy);
            const complete = privacy.filter(activity => !this.missingByActivity[activity.id].length).length;
            rows.push({
              type: 'group', key, name: group.name, icon: 'layer-group', accent: 'teal',
              meta: this.$tc('processing_map.map.group_meta', processes.length, { processes: processes.length, activities: activities.length }),
              legalBasis: _.uniq(_.flatMap(processes, process => process.legal_basis.map(this.legalBasisName))),
              dataSubjects: _.uniq(_.flatMap(activities, 'data_subjects')),
              systems: _.uniq(_.flatMap(activities, 'systems')),
              total: privacy.length,
              pct: privacy.length ? Math.round(complete / privacy.length * 100) : 0
            });
            if (this.isOpen(key) || this.searching) {
              rows.push.apply(rows, children);
            }
          });

          // Processes outside a process group: allowed, shown in a neutral section
          const ungroupedRows = _.flatMap(this.ungrouped, process => processRows(process, INDENT_PROCESS, false));
          if (ungroupedRows.length) {
            rows.push({
              type: 'section', section: 'ungrouped', key: 'section-ungrouped', icon: 'folder-open', accent: 'grey',
              name: this.$t('processing_map.ungrouped.title'),
              meta: this.$tc('processing_map.ungrouped.meta', this.ungrouped.length, { count: this.ungrouped.length }),
              text: this.$t('processing_map.ungrouped.text')
            });
            if (this.isOpen('section-ungrouped') || this.searching) {
              rows.push.apply(rows, ungroupedRows);
            }
          }
        }

        // Activities without a process: legacy data, shown as a warning
        {
          const orphans = this.orphans.filter(activity => matches(activity.name) && (this.tab !== 'missing' || this.missingByActivity[activity.id].length));
          if (orphans.length) {
            rows.push({
              type: 'section', section: 'unlinked', key: 'section-unlinked', icon: 'exclamation-triangle', accent: 'amber',
              name: this.$tc('processing_map.unlinked.title', orphans.length, { count: orphans.length }),
              meta: '',
              text: this.$t('processing_map.unlinked.text')
            });
            if (this.isOpen('section-unlinked') || this.searching || this.tab === 'unlinked') {
              orphans.forEach(activity => rows.push(this.activityRow(activity, null, INDENT_PROCESS)));
            }
          }
        }

        return rows;
      }
    },
    watch: {
      getClient: function(newValue, oldValue) {
        if (newValue && (!oldValue || newValue.slug !== oldValue.slug)) {
          this.open = {};
          this.loadMap();
        }
      }
    },
    methods: {
      loadMap() {
        let self = this;
        piincHttp.get('processing-map', { params: {
          client: this.getClient.slug
        }}).then(function(response) {
          self.map = response.data;
          self.loading = false;
        }, function() {
          self.loading = false;
        });
      },
      legalBasisName(article) {
        return article.short_name ? article.name + ' · ' + article.short_name : article.name;
      },
      activityRow(activity, process, indent) {
        const others = process
          ? activity.process_ids.filter(id => id !== process.id).map(id => this.processById[id]).filter(Boolean).map(other => other.name)
          : [];
        const missing = this.missingByActivity[activity.id];
        return {
          type: 'activity',
          key: 'activity-' + activity.id + '-' + (process ? process.id : 'none'),
          indent, activity,
          orphan: !process,
          alsoIn: others.join(', '),
          status: this.map.gdpr && activity.privacy
            ? (missing.length
              ? { ok: false, label: this.$tc('processing_map.status.missing', missing.length, { count: missing.length }) }
              : { ok: true, label: this.$t('processing_map.status.complete') })
            : null
        };
      },
      processStatus(process) {
        if (!this.map.gdpr || !process.privacy) {
          return null;
        }
        if (!process.legal_basis.length) {
          return { ok: false, label: this.$t('processing_map.status.no_legal_basis') };
        }
        const incomplete = process.activity_ids.filter(id => this.missingByActivity[id] && this.missingByActivity[id].length).length;
        return incomplete
          ? { ok: false, label: this.$tc('processing_map.status.incomplete_activities', incomplete, { count: incomplete }) }
          : { ok: true, label: this.$t('processing_map.status.complete') };
      },
      // Groups and the two sections start open, processes closed
      isOpen(key) {
        if (this.open[key] !== undefined) {
          return this.open[key];
        }
        return key.indexOf('group-') === 0 || key.indexOf('section-') === 0;
      },
      toggle(key) {
        this.$set(this.open, key, !this.isOpen(key));
      },
      toggleAll() {
        const value = !this.allOpen;
        const open = {};
        this.openableKeys.forEach(key => { open[key] = value; });
        this.open = open;
      },
      openAttention(item) {
        this.tab = item.tab;
        if (item.open) {
          this.$set(this.open, item.open, true);
        }
      },
      ringDash(done, total, radius) {
        const circumference = 2 * Math.PI * radius;
        return (circumference * done / total) + ' ' + circumference;
      },
      addProcess() {
        this.$refs.createProcess.showCreateSModal();
      },
      addActivity(process) {
        this.$refs.createActivity.open(process);
      },
      startProject() {
        this.$refs.createProject.showCreateProjectModal();
      },
      openLink(activity) {
        this.linkActivity = activity;
        this.linkProcess = null;
        this.linkError = '';
        this.$refs['link-modal'].show();
      },
      submitLink() {
        let self = this;
        let formData = new FormData();
        formData.append('process', this.linkProcess.slug);
        formData.append('activity', this.linkActivity.slug);
        this.linking = true;
        piincHttp.post('activities/' + this.linkActivity.slug + '/processes', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function() {
          self.linking = false;
          self.$refs['link-modal'].hide();
          self.$toastr('success', self.$t('processing_map.unlinked.linked', { name: self.linkActivity.name, process: self.linkProcess.name }));
          self.loadMap();
        }, function(error) {
          self.linking = false;
          self.linkError = error && error.data && error.data.message ? error.data.message : self.$t('processing_map.errors.link_failed');
        });
      },
      // ⌘K / Ctrl+K jumps to the filter
      onKeydown(event) {
        if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
          event.preventDefault();
          this.$refs.search.focus();
        }
      }
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.loadMap();
      }
    },
    mounted() {
      document.addEventListener('keydown', this.onKeydown);
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.onKeydown);
    }
  }
</script>
